import { useState, useEffect, useRef, useContext } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { serverTimestamp, updateDoc, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase.config';
import { useNavigate, useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import LoadingContext from '../contexts/loading/LoadingContext'
import { Helmet } from 'react-helmet'

import Spinner from '../components/feedback/Spinner'
import FormRadio from '../components/form/FormRadio'
import FormSelect from '../components/form/FormSelect'
import FormNumber from '../components/form/FormNumber';
import FormCheckbox from '../components/form/FormCheckbox';

import {
  Button,
  FormControl,
  FormLabel,
  Switch,
  Input,
  NumberInput,
  Textarea,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Heading,
  Radio,
  RadioGroup,
  useRadioGroup,
  Stack,
  HStack,
  useToast,
  Box,
  Text, Icon
} from '@chakra-ui/react'

import { cities } from '../data/citiesData';
import { FaHome, FaClock, FaMoneyBillWave } from 'react-icons/fa'

const EditPropertyListing = () => {
  // Hardcoded property types data
  const propertyTypes = [
    { type: 'House' },
    { type: 'Apartment' },
    { type: 'Villa' },
    { type: 'Commercial' },
    { type: 'Plot' },
    { type: 'Farmhouse' },
    { type: 'Penthouse' },
    { type: 'Studio' },
    { type: 'Townhouse' },
    { type: 'Office' },
    { type: 'Shop' },
    { type: 'Warehouse' }
  ];

  const [formData, setFormData] = useState({
    type: 'sale',
    propertyType: '',
    title: '',
    offer: false,
    bedrooms: '3',
    bathrooms: '2',
    yearBuilt: '2020',
    area: '',
    location: '',
    city: '',
    price: '',
    discountedPrice: '',
    description: '',
    images: {},
    features: {
      parking: false,
      garden: false,
      furnished: false,
      airConditioning: false,
      swimmingPool: false,
      security: false
    },
    userRef: ''
  })
  const { buttonLoading, spinnerLoading, dispatch } = useContext(LoadingContext)
  const [listing, setListing] = useState(false)

  const { getRootProps, getRadioProps} = useRadioGroup({
    name: 'propertyType',
    defaultValue: 'House'
  })
  const group = getRootProps()
  
  const toast = useToast()
  const isMounted = useRef(true)
  const auth = getAuth()
  const navigate = useNavigate()
  const params = useParams()
  
  const format = (val) => `PKR ` + val
  const parse = (val) => val.replace(/^\$/, '')
  
  useEffect(() => {
    if (isMounted) {
        onAuthStateChanged(auth, (user) => {
            if (user) {
                setFormData(prev => ({
                    ...prev,
                    userRef: user.uid
                }))
                dispatch({ type: 'STOP_SPINNER_LOADING' })
            } else {
                navigate('/sign-in')
            }
        })
    }
    return () => {
        isMounted.current = false
    }
  }, [isMounted])
  

  useEffect(() => {
    const fetchUserListing = async () => {
        const docRef = doc(db, 'properties', params.listingId)
        const docSnap = await getDoc(docRef)

        if (docSnap.exists()) {
            setListing(docSnap.data())
            setFormData({...docSnap.data()})
        } else {
            navigate('/')
            toast({
                title: 'Error',
                description: "The property listing does not exist",
                position: 'top',
                status: 'error',
                duration: 3000,
                isClosable: true,
            })
        }
    }
    fetchUserListing()
  }, [params.listingId, navigate])

  const changeHandler = (e) => {
      if (!e.target.files) {
        setFormData(prev => ({
            ...prev,
            [e.target.id]: e.target.value
        }))
      }
      if (e.target.files) {
          setFormData(prev => ({
              ...prev,
              images: e.target.files
          }))
      }
  }

  const submitHandler = async (e) => {
    e.preventDefault()
    dispatch({ type: 'START_LOADING' })

    try {
      let formDataCopy = {...formData}
      
      // Handle image upload if images changed
      if(formData.images !== listing.images) {
        const storeImage = async (img) => {
          return new Promise((resolve, reject) => {
              const storage = getStorage()
              const fileName = `${auth.currentUser.uid}-${img.name}-${uuidv4()}`
              const storageRef = ref(storage, 'property-images/' + fileName)
              const uploadTask = uploadBytesResumable(storageRef, img)

              uploadTask.on('state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                console.log('Upload is ' + progress + '% done')
              }, 
              (error) => {
                reject(error)
              }, 
              () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                  resolve(downloadURL)
                })
              })
          })
        }

        const propertyImages = await Promise.all(
            [...formData.images].map(img => storeImage(img))
        ).catch((error) => {
            alert(error)
            return
        })
        
        formDataCopy = {
          ...formDataCopy,
          propertyImages,
          timestamp: serverTimestamp()
        }
      }

      delete formDataCopy.images

      const docRef = doc(db, 'properties', params.listingId)
      await updateDoc(docRef, formDataCopy)

      dispatch({ type: 'STOP_LOADING' })
      toast({
        title: 'Success',
        description: "Your property listing has been updated successfully",
        position: 'top',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      navigate(`/category/${formDataCopy.type}/${docRef.id}`)

    } catch (error) {
      console.log(error.code)
      dispatch({ type: 'STOP_LOADING' })
      toast({
        title: 'Error',
        description: "Something went wrong!",
        position: 'top',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  if (spinnerLoading) {
      return <Spinner />
  }

  return (
    <>
      <Helmet>
        <title>Edit Property Listing | NCPProperties</title>
        <meta
          name="keywords"
          content="ncp properties, real estate, property for sale, property for rent, houses for sale, apartments for rent, commercial property, residential property, pakistan real estate, property investment"
        />
        <link rel="canonical" href="https://www.estateghar.com/edit-listing/{params.listingId}" />
      </Helmet>
      
      <form onSubmit={submitHandler}>
        <Box maxW="container.lg" mx="auto" p={4}>
          <Heading mb={8} display="flex" alignItems="center">
            <Icon as={formData.type === 'rent' ? FaClock : formData.type === 'commercial' ? FaMoneyBillWave : FaHome} 
                  mr={3} 
                  color="blue.500" />
            Edit {formData.type === 'rent' ? 'Rental' : formData.type === 'commercial' ? 'Commercial' : 'Sale'} Property Listing
          </Heading>

          <FormSelect
            name='Property Type'
            id='propertyType'
            value={formData.propertyType}
            handler={changeHandler}
            selectData={propertyTypes}
            dataKey='type'
          />

          <FormControl mb={4}>
            <FormLabel>Property Title</FormLabel>
            <Input
              variant='flushed'
              id='title'
              type='text'
              value={formData.title}
              onChange={changeHandler}
              placeholder='ex. Beautiful 3-Bedroom House in DHA'
              isRequired
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Listing Type</FormLabel>
            <RadioGroup 
              id='type' 
              onChange={(value) => setFormData(prev => ({
                ...prev,
                type: value
              }))} 
              value={formData.type}
            >
              <Stack spacing={5} direction='row'>
                <Radio colorScheme='teal' value='sale'>For Sale</Radio>
                <Radio colorScheme='teal' value='rent'>For Rent</Radio>
                <Radio colorScheme='teal' value='commercial'>Commercial</Radio>
              </Stack>
            </RadioGroup> 
          </FormControl>

          <FormNumber
            labelText='Bedrooms'
            inputValue={formData.bedrooms}
            max='20'
            min='0'
            onChange={(valueString) => setFormData(prev => ({
              ...prev,
              bedrooms: valueString
            }))} 
          />
          
          <FormNumber
            labelText='Bathrooms'
            inputValue={formData.bathrooms}
            max='10'
            min='0'
            onChange={(valueString) => setFormData(prev => ({
              ...prev,
              bathrooms: valueString
            }))} 
          />

          <FormNumber
            labelText='Year Built'
            inputValue={formData.yearBuilt}
            max='2024'
            min='1800'
            onChange={(valueString) => setFormData(prev => ({
              ...prev,
              yearBuilt: valueString
            }))} 
          />
          
          <FormNumber
            labelText='Area (sq ft)'
            inputValue={formData.area}
            min='0'
            onChange={(valueString) => setFormData(prev => ({
              ...prev,
              area: valueString
            }))} 
          />     
          
          {/* Single Price Field */}
          <FormNumber
            labelText={formData.type === 'rent' ? 'Monthly Rent' : formData.type === 'commercial' ? 'Lease Price' : 'Sale Price'}
            inputValue={formData.price}
            min='0'
            onChange={(value) => setFormData(prev => ({
              ...prev,
              price: parse(value)
            }))}
          /> 

          {formData.type === 'sale' && (
            <>
              <FormControl mb={4} display='flex' alignItems='center'>
                <FormLabel htmlFor='offer' mb='0'>
                  Discount Available
                </FormLabel>
                <Switch 
                  colorScheme='teal' 
                  id='offer' 
                  size='lg' 
                  isChecked={formData.offer} 
                  onChange={() => setFormData(prev => ({
                    ...prev,
                    offer: !prev.offer
                  }))}        
                />
              </FormControl>
              
              {formData.offer && (
                <FormControl mb={4}>
                  <FormLabel htmlFor='discountedPrice'>Discounted Price</FormLabel>
                  <NumberInput
                    variant='flushed'
                    min={0}
                    max={formData.price} 
                    onChange={(value) => setFormData(prev => ({
                      ...prev,
                      discountedPrice: parse(value)
                    }))} 
                    value={formData.discountedPrice}
                  >
                    <NumberInputField id='discountedPrice' />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              )}
            </>
          )}

          <FormSelect
            name='City'
            id='city'
            value={formData.city}
            handler={changeHandler}
            selectData={cities}
            dataKey='city'
          />
          
          <FormControl mb={4}>
            <FormLabel htmlFor='location'>Address</FormLabel>
            <Input
              variant='flushed' 
              value={formData.location} 
              onChange={changeHandler} 
              id='location' 
              type='text' 
              placeholder='Full address including street, sector, etc.'
              isRequired
            />
          </FormControl> 
          
          <FormControl mb={4}>
            <FormLabel htmlFor='description'>Property Description</FormLabel>
            <Textarea
              variant='flushed'
              id='description'
              value={formData.description}
              onChange={changeHandler}
              placeholder='Describe your property in detail...'
              size='sm'
              rows={4}
            />
          </FormControl>

          <FormControl mb={6}>
            <FormLabel>Property Features</FormLabel>
            <Stack direction={['column', 'row']} spacing='15px' wrap="wrap">
              <FormCheckbox
                checkboxValue='parking'
                checkStatus={formData.features.parking}
                text='Parking'
                change={() => setFormData(prev => ({
                  ...prev,
                  features: {
                    ...prev.features,
                    parking: !prev.features.parking
                  }
                }))}
              />
              <FormCheckbox
                checkboxValue='garden'
                checkStatus={formData.features.garden}
                text='Garden'
                change={() => setFormData(prev => ({
                  ...prev,
                  features: {
                    ...prev.features,
                    garden: !prev.features.garden
                  }
                }))}
              />
              <FormCheckbox
                checkboxValue='furnished'
                checkStatus={formData.features.furnished}
                text='Furnished'
                change={() => setFormData(prev => ({
                  ...prev,
                  features: {
                    ...prev.features,
                    furnished: !prev.features.furnished
                  }
                }))}
              />
              <FormCheckbox
                checkboxValue='airConditioning'
                checkStatus={formData.features.airConditioning}
                text='Air Conditioning'
                change={() => setFormData(prev => ({
                  ...prev,
                  features: {
                    ...prev.features,
                    airConditioning: !prev.features.airConditioning
                  }
                }))}
              />
              <FormCheckbox
                checkboxValue='swimmingPool'
                checkStatus={formData.features.swimmingPool}
                text='Swimming Pool'
                change={() => setFormData(prev => ({
                  ...prev,
                  features: {
                    ...prev.features,
                    swimmingPool: !prev.features.swimmingPool
                  }
                }))}
              />
              <FormCheckbox
                checkboxValue='security'
                checkStatus={formData.features.security}
                text='Security'
                change={() => setFormData(prev => ({
                  ...prev,
                  features: {
                    ...prev.features,
                    security: !prev.features.security
                  }
                }))}
              />
            </Stack>
          </FormControl>

          <FormControl mb={6}>
            <FormLabel>Property Images</FormLabel>
            <Text fontSize="sm" color="gray.500" mb={2}>
              First image will be used as cover (max 10)
            </Text>
            <Input
              type='file'
              id='images'
              accept='.jpg,.png,.jpeg'
              multiple
              onChange={changeHandler}
              border="none"
              p={1}
            />
          </FormControl>
          
          <Button 
            isLoading={buttonLoading}
            loadingText='Updating...'
            type='submit' 
            colorScheme='teal' 
            size='lg'
            width="full"
            mb={4}
          >
            Update Property Listing
          </Button>
        </Box>
      </form>
    </>
  )
}

export default EditPropertyListing